import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSupabaseAuth } from '../hooks/useSupabaseAuth'
import { supabase } from '../lib/supabase.js'

function getInitials(user) {
  const first = user?.user_metadata?.firstName || user?.user_metadata?.first_name || user?.email?.[0]
  const last = user?.user_metadata?.lastName || user?.user_metadata?.last_name
  const initials = [first, last].filter(Boolean).map((part) => String(part).charAt(0).toUpperCase())
  return initials.join('') || 'U'
}

export default function AuthButton() {
  const { isSignedIn, user, isAdmin } = useSupabaseAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const initials = useMemo(() => (user ? getInitials(user) : 'U'), [user])
  const canAccessAdmin = isAdmin
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [menuOpen])

  const handleSignIn = () => {
    navigate(`/sign-in?redirect=${encodeURIComponent(location.pathname + location.search)}`)
  }

  const handleSignUp = () => {
    navigate(`/sign-up?redirect=${encodeURIComponent(location.pathname + location.search)}`)
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    setMenuOpen(false)
    navigate('/', { replace: true })
  }

  if (isSignedIn && user) {
    return (
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen((open) => !open)}
          className="w-9 h-9 rounded-full bg-yellow-500 text-gray-900 font-semibold flex items-center justify-center border border-yellow-400 shadow-[0_0_18px_rgba(234,179,8,0.35)] focus:outline-none focus:ring-2 focus:ring-yellow-500 ring-offset-2 ring-offset-slate-900"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          {initials}
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-44 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl py-2 z-50">
            <span className="block px-4 py-2 text-xs text-slate-400 uppercase tracking-wide">
              {user.email}
            </span>
            {canAccessAdmin && (
              <button
                onClick={() => {
                  navigate('/admin')
                  setMenuOpen(false)
                }}
                className="w-full text-left px-4 py-2 text-sm text-slate-100 hover:bg-slate-800 transition"
              >
                Admin Panel
              </button>
            )}
            <button
              onClick={handleSignOut}
              className="w-full text-left px-4 py-2 text-sm text-slate-100 hover:bg-slate-800 transition"
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    )
  }

  // Show custom sign-in/sign-up buttons for public users
  return (
    <div className="flex items-center space-x-2">
      <button
        onClick={handleSignIn}
        className="px-4 py-2 text-sm font-medium text-white bg-transparent border border-white rounded-md hover:bg-white hover:text-gray-900 transition-colors"
      >
        Sign In
      </button>
      <button
        onClick={handleSignUp}
        className="px-4 py-2 text-sm font-medium text-gray-900 bg-yellow-500 border border-yellow-500 rounded-md hover:bg-yellow-400 hover:border-yellow-400 transition-colors"
      >
        Sign Up
      </button>
    </div>
  )
}
